import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileUploadService } from 'src/app/core/file-upload';
import { normalizeName } from 'src/app/core/v3/utils/normalize-name';
import { Invoice, InvoicePayload } from 'src/app/core/v2/types/invoice';
import { FileDropService } from '../../../shared_components/upload-file/file-drop-dialog/file-drop.service';

export interface InvoiceDialogInput {
  title: string;
  currency: string;
  invoice: Invoice | null;
}

export type InvoiceDialogReturn = InvoicePayload;

@Component({
  selector: 'kt-invoice-dialog',
  templateUrl: './invoice-dialog.component.html'
})
export class InvoiceDialogComponent implements OnInit {
  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

  form: UntypedFormGroup;
  uploading = false;

  constructor(
    public dialogRef: MatDialogRef<InvoiceDialogComponent, InvoiceDialogReturn>,
    @Inject(MAT_DIALOG_DATA) public data: InvoiceDialogInput,
    private fb: UntypedFormBuilder,
    private fileUploadService: FileUploadService,
    private fileDrop: FileDropService,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      invoice_number: [this.data.invoice ? this.data.invoice.invoice_number : '', Validators.required],
      amount: [this.data.invoice ? this.data.invoice.amount : null, Validators.required],
      file_name: [this.data.invoice ? this.data.invoice.file_name : null],
      file_path: [this.data.invoice ? this.data.invoice.file_path : null]
    });
  }

  /** Name of the picked file, without the timestamp prefix the upload endpoint adds. */
  get fileDisplayName(): string | null {
    return normalizeName(this.form.controls.file_name.value);
  }

  onClickUpload() {
    this.fileDrop.pick({
      title: 'Upload File',
      accept: '.pdf,.jpeg,.jpg,.png,.doc,.docx,.xls,.xlsx',
      multiple: false
    }).subscribe(files => this.handleFiles(files));
  }

  onUpload(event: any) {
    this.handleFiles(Array.from(event.target.files as FileList));
    event.srcElement.value = null;
  }

  private handleFiles(files: File[]) {
    if (!files || files.length == 0) {
      return;
    }
    const formData = new FormData();
    formData.append('files[0]', files[0], files[0].name);
    formData.append('has_prefix', 'true');

    this.uploading = true;
    this.fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      this.uploading = false;
      const body = value.body as any;
      const uploaded = body.uploaded_files[0];
      this.form.patchValue({
        file_name: uploaded.name,
        file_path: uploaded.path
      });
    }, () => {
      this.uploading = false;
    });
  }

  removeFile() {
    this.form.patchValue({file_name: null, file_path: null});
  }

  onNoClick() {
    this.dialogRef.close();
  }

  save() {
    const controls = this.form.controls;
    if (this.form.invalid) {
      Object.keys(controls).forEach(controlName => {
        controls[controlName].markAllAsTouched();
      })
      return;
    }

    this.dialogRef.close({
      invoice_number: controls.invoice_number.value,
      amount: Number(controls.amount.value),
      file_name: controls.file_name.value,
      file_path: controls.file_path.value
    });
  }
}
